import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSessionContext } from "./SessionContext";
import { useLocationContext } from "./LocationContext";
import { useCachedResource } from "src-next/shared/cache/useCachedResource";
import {
  appointmentPatientsCacheKey,
  consentPatientsCacheKey,
  fetchAppointmentPatients,
  fetchConsentPatients,
  fetchOrganizationPersons,
  organizationPersonsCacheKey,
} from "src-next/modules/organization-patients";

interface PatientOrganizationContextValue {
  patients: fhir5.BundleEntry<fhir5.Patient>[] | undefined;
  refetchPatientOrganization: Function;
  patientOrganizationLoading: boolean;
  /**
   * Campos novos (opcionais para não exigir mudança nos consumidores atuais).
   * `patientOrganizationRefreshing`: pedido em andamento com a lista já na tela.
   */
  patientOrganizationRefreshing?: boolean;
  /**
   * Pacientes com consentimento e clientes cadastrados (Person).
   * Só são baixados depois de `request*()`: nada disso roda no boot.
   */
  consentPatients?: fhir5.Patient[];
  consentPatientsLoading?: boolean;
  requestConsentPatients?: () => void;
  refreshConsentPatients?: () => Promise<void>;
  registeredPersons?: fhir5.Person[];
  registeredPersonsLoading?: boolean;
  requestRegisteredPersons?: () => void;
  refreshRegisteredPersons?: () => Promise<void>;
}

export const PatientOrganizationContext =
  createContext<PatientOrganizationContextValue>(null!);

// Referências estáveis: evitam re-render nos consumidores que usam `useMemo`.
const EMPTY_PATIENT_ENTRIES: fhir5.BundleEntry<fhir5.Patient>[] = [];
const EMPTY_PATIENTS: fhir5.Patient[] = [];
const EMPTY_PERSONS: fhir5.Person[] = [];

export function PatientOrganizationContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { access } = useSessionContext();
  const { location, locationLoading } = useLocationContext();

  const isProfessional = access?.type === "professional";
  const orgId = access?.organizationId;
  const hasLocations = Array.isArray(location) && location.length > 0;

  const locationIds = useMemo(
    () =>
      (location ?? []).map(
        (element: fhir5.BundleEntry<fhir5.Location>) => element.resource?.id,
      ),
    [location],
  );

  /**
   * Chave da lista base (pacientes atendidos).
   * - A chave inclui a organização. Trocar de org cai numa chave vazia, então
   *   a lista anterior nunca aparece (segurança / LGPD).
   * - Os ids de Location são ordenados na chave: uma nova referência do array
   *   `location` com os mesmos ids não dispara outro pedido de 20000 itens.
   * - Enquanto as locations recarregam na MESMA org, mantemos a última chave
   *   para não piscar a lista (o comportamento antigo também a mantinha).
   */
  const lastResolvedRef = useRef<{ orgId?: string; key: string | null }>({
    key: null,
  });
  let cacheKey: string | null = null;
  if (isProfessional && !locationLoading && hasLocations) {
    cacheKey = appointmentPatientsCacheKey(orgId, locationIds);
    lastResolvedRef.current = { orgId, key: cacheKey };
  } else if (
    isProfessional &&
    locationLoading &&
    lastResolvedRef.current.orgId === orgId
  ) {
    cacheKey = lastResolvedRef.current.key;
  }

  const base = useCachedResource<fhir5.BundleEntry<fhir5.Patient>[]>({
    cacheKey,
    fetcher: () => fetchAppointmentPatients(locationIds),
  });

  /**
   * Mesmas regras de antes:
   * - Sem locations (já carregadas): lista vazia, sem pedido.
   * - Erro na busca: lista vazia (antes o `catch` também devolvia `[]`).
   *   O erro fica no cache, sem dado; a próxima montagem tenta de novo.
   */
  const noLocations =
    isProfessional && !locationLoading && !hasLocations;
  const patients = noLocations
    ? EMPTY_PATIENT_ENTRIES
    : base.data ?? (base.error ? EMPTY_PATIENT_ENTRIES : undefined);

  // `refresh` mantém a lista atual na tela; só este flag indica o pedido.
  const [refreshing, setRefreshing] = useState(false);
  const refreshBase = base.refresh;
  const refetchPatientOrganization = useCallback(async () => {
    setRefreshing(true);
    try {
      // `force: true` dentro do `refresh`: ignora a memória e busca de novo.
      await refreshBase();
    } catch (err) {
      console.log("PatientOrganization", err);
    } finally {
      setRefreshing(false);
    }
  }, [refreshBase]);

  /**
   * Públicos sob demanda. O latch só vai de false para true: depois do
   * primeiro pedido, fechar o dialog e abrir de novo só relê o cache.
   */
  const [consentRequested, setConsentRequested] = useState(false);
  const [personsRequested, setPersonsRequested] = useState(false);

  // O CNPJ do consentimento é o próprio `access.organizationId` (só dígitos).
  const consent = useCachedResource<fhir5.Patient[]>({
    cacheKey: isProfessional ? consentPatientsCacheKey(orgId) : null,
    enabled: consentRequested,
    fetcher: () => fetchConsentPatients(orgId ?? ""),
  });

  // Mesma chave do `useCommunicationsOrganizationPersons`: um pedido só.
  const persons = useCachedResource<fhir5.Person[]>({
    cacheKey: isProfessional ? organizationPersonsCacheKey(orgId) : null,
    enabled: personsRequested,
    fetcher: () => fetchOrganizationPersons(orgId ?? ""),
  });

  const requestConsentPatients = useCallback(
    () => setConsentRequested(true),
    [],
  );
  const requestRegisteredPersons = useCallback(
    () => setPersonsRequested(true),
    [],
  );

  const refreshConsent = consent.refresh;
  const refreshConsentPatients = useCallback(async () => {
    setConsentRequested(true);
    // Org sem consentimento (ex.: não-ecommerce) não pode quebrar a tela.
    await refreshConsent().catch(() => undefined);
  }, [refreshConsent]);

  const refreshPersons = persons.refresh;
  const refreshRegisteredPersons = useCallback(async () => {
    setPersonsRequested(true);
    await refreshPersons().catch(() => undefined);
  }, [refreshPersons]);

  const contextValue = useMemo<PatientOrganizationContextValue>(
    () => ({
      patients,
      refetchPatientOrganization,
      patientOrganizationLoading: base.isLoading,
      patientOrganizationRefreshing: refreshing,
      // Falha ou org sem dados: lista vazia, nunca erro na UI.
      consentPatients: consent.data ?? EMPTY_PATIENTS,
      consentPatientsLoading: consentRequested && consent.isLoading,
      requestConsentPatients,
      refreshConsentPatients,
      registeredPersons: persons.data ?? EMPTY_PERSONS,
      registeredPersonsLoading: personsRequested && persons.isLoading,
      requestRegisteredPersons,
      refreshRegisteredPersons,
    }),
    [
      patients,
      refetchPatientOrganization,
      base.isLoading,
      refreshing,
      consent.data,
      consent.isLoading,
      consentRequested,
      requestConsentPatients,
      refreshConsentPatients,
      persons.data,
      persons.isLoading,
      personsRequested,
      requestRegisteredPersons,
      refreshRegisteredPersons,
    ],
  );

  return (
    <PatientOrganizationContext.Provider value={contextValue}>
      {children}
    </PatientOrganizationContext.Provider>
  );
}

export function usePatientOrganizationContext() {
  return useContext(PatientOrganizationContext);
}
